import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faTimes,
  faCheck,
  faClock,
  faHistory,
  faKeyboard,
} from "@fortawesome/free-solid-svg-icons";
import "react-datepicker/dist/react-datepicker.css";

const SHORTCUTS = {
  TODAY: "T",
  YESTERDAY: "Y",
  LAST_WEEK: "W",
  CLEAR: "C",
  SAVE: "Enter",
  CANCEL: "Escape",
  NOW: "N",
};

// Enhanced editable cell component with keyboard shortcuts and double-click
const EditableCell = ({
  type,
  value,
  onChange,
  onSave,
  onCancel,
  options = [],
  isEditing = false,
  onStartEditing = () => {},
  fieldName = "",
  showShortcutsHelp = false,
}) => {
  // Initialize state with the current value
  const [cellValue, setCellValue] = useState(value);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [internalEditing, setInternalEditing] = useState(isEditing);
  const [showShortcuts, setShowShortcuts] = useState(showShortcutsHelp);
  const datePickerRef = useRef(null);
  const cellRef = useRef(null);

  // Update local state when value prop changes
  useEffect(() => {
    setCellValue(value);
  }, [value]);

  // Update editing state when isEditing prop changes
  useEffect(() => {
    setInternalEditing(isEditing);
  }, [isEditing]);

  // Handle value changes
  const handleChange = (newValue) => {
    setCellValue(newValue);
    onChange(newValue);
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    handleChange(date);
    // Keep date picker open for further adjustments
  };

  // Handle save action
  const handleSave = () => {
    if (onSave) {
      onSave(cellValue);
    }
    setInternalEditing(false);
    setShowDatePicker(false);
  };

  // Handle cancel action
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setInternalEditing(false);
    setShowDatePicker(false);
  };

  // Handle double-click to start editing
  const handleDoubleClick = () => {
    if (!internalEditing) {
      onStartEditing();
      setInternalEditing(true);
      if (type === "date") {
        setShowDatePicker(true);
      }
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (!internalEditing) return;

    // Global shortcuts
    switch (e.key) {
      case SHORTCUTS.SAVE:
        e.preventDefault();
        handleSave();
        break;
      case SHORTCUTS.CANCEL:
        e.preventDefault();
        handleCancel();
        break;
      default:
        break;
    }

    // Date-specific shortcuts
    if (type === "date") {
      const now = new Date();

      switch (e.key.toUpperCase()) {
        case SHORTCUTS.TODAY:
          e.preventDefault();
          // Set to today at current time
          handleChange(now);
          break;
        case SHORTCUTS.YESTERDAY:
          e.preventDefault();
          // Set to yesterday at current time
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          handleChange(yesterday);
          break;
        case SHORTCUTS.LAST_WEEK:
          e.preventDefault();
          // Set to last week at current time
          const lastWeek = new Date();
          lastWeek.setDate(lastWeek.getDate() - 7);
          handleChange(lastWeek);
          break;
        case SHORTCUTS.CLEAR:
          e.preventDefault();
          // Clear the date
          handleChange(null);
          break;
        case SHORTCUTS.NOW:
          e.preventDefault();
          // Set to now
          handleChange(new Date());
          break;
        default:
          break;
      }
    }
  };

  // Handle click outside to close date picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [datePickerRef]);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Determine if we should show the cell in edit mode
  const shouldShowEditMode = internalEditing || isEditing;

  // Helper function to get custom date input
  const getCustomDateInput = ({ value, onClick }) => (
    <div className="flex items-center justify-between w-full px-2 py-1 border border-blue-300 rounded cursor-pointer bg-white">
      <span className={`${!value ? "text-gray-400" : ""}`}>
        {value || "Select date..."}
      </span>
      <div className="flex items-center">
        <FontAwesomeIcon
          icon={faKeyboard}
          className="text-blue-400 mr-2 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setShowShortcuts(!showShortcuts);
          }}
          title="Show keyboard shortcuts"
        />
        <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-500" />
      </div>
    </div>
  );

  // Cell content based on type
  const renderCellContent = () => {
    if (!shouldShowEditMode) {
      return (
        <div
          className="px-2 py-1 rounded hover:bg-blue-50 cursor-pointer transition-colors duration-150"
          onDoubleClick={handleDoubleClick}
          ref={cellRef}
          title={`Double-click to edit ${fieldName || ""}`}
        >
          {type === "date" ? formatDate(cellValue) : String(cellValue || "")}
        </div>
      );
    }

    switch (type) {
      case "text":
        return (
          <input
            type="text"
            value={cellValue || ""}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
        );

      case "select":
        return (
          <select
            value={cellValue || ""}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          >
            <option value="">- Select -</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "date":
        return (
          <div className="relative" ref={datePickerRef}>
            <div
              className="flex items-center justify-between w-full px-2 py-1 border border-blue-300 rounded cursor-pointer bg-white"
              onClick={() => setShowDatePicker(true)}
              onKeyDown={handleKeyDown}
              tabIndex={0}
            >
              <span className={`${!cellValue ? "text-gray-400" : ""}`}>
                {cellValue ? formatDate(cellValue) : "Select date..."}
              </span>
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faKeyboard}
                  className="text-blue-400 mr-2 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowShortcuts(!showShortcuts);
                  }}
                  title="Show keyboard shortcuts"
                />
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-blue-500"
                />
              </div>
            </div>

            {showShortcuts && (
              <div className="absolute z-20 mt-1 w-64 bg-white border border-gray-200 rounded shadow-lg p-2 text-xs">
                <div className="font-medium text-gray-700 mb-1 pb-1 border-b">
                  Keyboard Shortcuts:
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      T
                    </span>{" "}
                    Today
                  </div>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      Y
                    </span>{" "}
                    Yesterday
                  </div>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      W
                    </span>{" "}
                    Last Week
                  </div>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      N
                    </span>{" "}
                    Now (current time)
                  </div>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      C
                    </span>{" "}
                    Clear
                  </div>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      Enter
                    </span>{" "}
                    Save
                  </div>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-1 rounded text-gray-800 font-mono mr-1">
                      Esc
                    </span>{" "}
                    Cancel
                  </div>
                </div>
              </div>
            )}

            {showDatePicker && (
              <div className="absolute z-10 mt-1 bg-white shadow-md rounded border border-gray-200">
                <div className="p-2 border-b border-gray-200 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Select Date & Time
                  </span>
                  <div className="flex items-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange(new Date());
                      }}
                      className="mr-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
                      title="Set to current date and time"
                    >
                      <FontAwesomeIcon icon={faClock} className="mr-1" />
                      Now
                    </button>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
                <DatePicker
                  selected={cellValue ? new Date(cellValue) : null}
                  onChange={handleDateSelect}
                  inline
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MM/dd/yyyy h:mm aa"
                  timeCaption="Time"
                />
                <div className="p-2 border-t border-gray-200 flex justify-between">
                  <button
                    onClick={() => handleChange(null)}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
                  >
                    Clear
                  </button>
                  <div>
                    <button
                      onClick={handleCancel}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-800 rounded hover:bg-gray-300 mr-2"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "number":
        return (
          <input
            type="number"
            value={cellValue || ""}
            onChange={(e) => handleChange(parseFloat(e.target.value))}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
        );

      case "boolean":
        return (
          <select
            value={String(cellValue)}
            onChange={(e) => handleChange(e.target.value === "true")}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      default:
        return <div>Unsupported cell type</div>;
    }
  };

  return <div className="min-w-[180px] relative">{renderCellContent()}</div>;
};

export default EditableCell;
